import { deleteData, getData, postData, putData } from "../api/api";

/** Super Admin → Integrations: external systems that create users and sign them in to an embedded chat. */

export type IntegrationSecret = {
  id: string;
  prefix: string;
  createdAt?: string | null;
  createdBy?: string | null;
  graceUntil?: string | null;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
  active: boolean;
};

/** Workspace role the provisioned end users get; the chatbot needs no more than USER. */
export type IntegrationMemberRole = "TENANT_ADMIN" | "USER" | "REPORTER" | "VIEWER";

export const MEMBER_ROLES: IntegrationMemberRole[] = ["USER", "REPORTER", "VIEWER", "TENANT_ADMIN"];

export type IntegrationClient = {
  id: string;
  name: string;
  enabled: boolean;
  /** The customer workspace whose data this integration's end users query. */
  workspaceId?: string | null;
  workspaceName?: string | null;
  memberRole?: IntegrationMemberRole | null;
  /** End users may only use the embedded chatbot, never the Connect AI screens. */
  chatbotOnly: boolean;
  embedConfigId: string;
  embedName?: string | null;
  embedPublicKey?: string | null;
  allowedIps: string[];
  rateLimitPerMinute?: number | null;
  effectiveRateLimitPerMinute: number;
  sendWelcomeMail: boolean;
  autoSetupBeas: boolean;
  secrets: IntegrationSecret[];
  userCount: number;
  createdDate?: string | null;
  createdBy?: string | null;
};

export type IntegrationClientPayload = {
  name: string;
  enabled: boolean;
  workspaceId: string;
  memberRole: IntegrationMemberRole;
  chatbotOnly: boolean;
  embedConfigId: string;
  allowedIps: string[];
  rateLimitPerMinute: number | null;
  sendWelcomeMail: boolean;
  autoSetupBeas: boolean;
};

export type IntegrationSecretCreated = { secret: string; info: IntegrationSecret };

export type IntegrationSetupStatus = "NOT_REQUESTED" | "PENDING" | "COMPLETE" | "FAILED";

export type IntegrationUser = {
  id: string;
  externalUserId?: string | null;
  email: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  workspaceId?: string | null;
  /** Value this user's rows carry in the workspace's data isolation field. */
  isolationValue?: string | null;
  chatbotOnly: boolean;
  enabled: boolean;
  setupStatus: IntegrationSetupStatus;
  setupError?: string | null;
  createdAt?: string | null;
  lastTicketAt?: string | null;
  lastLoginAt?: string | null;
};

export type IntegrationUserPage = {
  content: IntegrationUser[];
  totalElements: number;
  number: number;
  size: number;
};

export type IntegrationStats = {
  totalUsers: number;
  enabledUsers: number;
  setupComplete: number;
  setupPending: number;
  setupFailed: number;
  usersWithTicketLast30Days: number;
  usersSignedInLast30Days: number;
  lastTicketAt?: string | null;
  lastLoginAt?: string | null;
};

const BASE = "/admin/integrations";
const item = (id: string) => `${BASE}/${encodeURIComponent(id)}`;

export const integrationClientService = {
  list: () => getData(BASE) as Promise<IntegrationClient[]>,
  create: (payload: IntegrationClientPayload) => postData(BASE, payload) as Promise<IntegrationClient>,
  update: (id: string, payload: IntegrationClientPayload) => putData(item(id), payload) as Promise<IntegrationClient>,
  remove: (id: string) => deleteData(item(id)) as Promise<void>,
  createSecret: (id: string) => postData(`${item(id)}/secrets`, {}) as Promise<IntegrationSecretCreated>,
  rotateSecret: (id: string, secretId: string, graceDays: number) =>
    postData(
      `${item(id)}/secrets/${encodeURIComponent(secretId)}/rotate?graceDays=${encodeURIComponent(String(graceDays))}`,
      {}
    ) as Promise<IntegrationSecretCreated>,
  revokeSecret: (id: string, secretId: string) =>
    deleteData(`${item(id)}/secrets/${encodeURIComponent(secretId)}`) as Promise<IntegrationClient>,
  users: (id: string, page = 0, size = 25) =>
    getData(`${item(id)}/users?page=${page}&size=${size}`) as Promise<IntegrationUserPage>,
  retrySetup: (id: string, userId: string) =>
    postData(`${item(id)}/users/${encodeURIComponent(userId)}/setup`, {}) as Promise<IntegrationUser>,
  stats: (id: string) => getData(`${item(id)}/stats`) as Promise<IntegrationStats>,
};
